import React, { useEffect, useState, useRef } from 'react';
import { hot } from 'react-hot-loader';
import Heading from '../Heading/Heading';
import LazyImage from '../../../hocs/Lazy/LazyImage';
import { fillArray } from '../../../utils/array';
import { addEvent, removeEvent, height, offset } from '../../../utils/dom';
import { imageSizeTranslation } from '../../../utils/image';
import { translate } from '../../../utils/locale';
import s from './DoubleImages.scss';

const DoubleImages = ({ setup, content }) => {
	const crossFadeContainer = useRef(null);
	const [percentageScrolled, updatePercentageScrolled] = useState(0);

	const divideByX = (amount) => {
		const divisions = fillArray(amount, () => 0);
		const dividedScroll = percentageScrolled * amount;

		divisions[Math.floor(dividedScroll)] = dividedScroll - Math.floor(dividedScroll);

		for (let i = 0; i < Math.floor(dividedScroll); i += 1) {
			divisions[i] = 1;
		}

		return divisions;
	};

	const scrollPart = content.statistics ? divideByX((setup.isMobile ? 3 : 2)) : [];

	useEffect(() => {
		let crossFadeContainerPosition = offset(crossFadeContainer.current).top;
		let windowHeight = height(window);
		let crossFadeContainerHeight = windowHeight * 2;

		const animateCrossFadeScroll = () => {
			const top = window.pageYOffset || document.documentElement.scrollTop;
			if (top > crossFadeContainerPosition && top < (crossFadeContainerPosition + crossFadeContainerHeight)) {
				// (windowHeight - crossFadeContainerHeight) / top - crossFadeContainerPosition === 100
				// top === crossFadeContainerPosition === 0
				// (top - crossFadeContainerPosition) === (windowHeight - crossFadeContainerHeight) === 197
				let position = (top - crossFadeContainerPosition) / (crossFadeContainerHeight - windowHeight);
				if (position > 1) position = 1;
				if (position < 0) position = 0;

				updatePercentageScrolled(Math.round(position * 100) / 100);
			} else {
				updatePercentageScrolled(0);
			}
		};

		const animateCrossFadeResize = () => {
			crossFadeContainerPosition = offset(crossFadeContainer.current).top;
			windowHeight = height(window);
			crossFadeContainerHeight = windowHeight * 3;
		};

		addEvent(window, 'scroll.doubleImages', () => {
			window.requestAnimationFrame(animateCrossFadeScroll);
		});

		addEvent(window, 'resize.doubleImages', () => {
			window.requestAnimationFrame(animateCrossFadeResize);
		});

		return () => {
			removeEvent(window, 'resize.doubleImages');
			removeEvent(window, 'scroll.doubleImages');
		};
	}, [crossFadeContainer]);

	return (
		<div className={s.doubleImages}>
			<div className={s.doubleImages_inner} ref={crossFadeContainer}>
				<h1 className={s.doubleImages_inner_title}>{ translate(setup, content.title) }</h1>
				<div className={s.doubleImages_inner_images}>
					{
						content.images && content.images.map((image) => (
							<figure key={image?.from?.url} className={s.doubleImages_inner_image}>
								{ image?.from?.url && <LazyImage {...imageSizeTranslation('full', image.from)} className={['js-lazyLoad', s.doubleImages_inner_image_src, s.doubleImages_inner_image_src__from]} style={{ opacity: 1 - percentageScrolled }} /> }
								{ image?.to?.url && <LazyImage {...imageSizeTranslation('full', image.to)} className={['js-lazyLoad', s.doubleImages_inner_image_src, s.doubleImages_inner_image_src__to]} style={{ opacity: percentageScrolled }} /> }
								{ image?.caption && <figcaption className={s.doubleImages_inner_image_caption}>{ translate(setup, image.caption) }</figcaption> }
							</figure>
						))
					}
				</div>
				<div className={s.doubleImages_inner_statistics}>
					{
						content.statistics && content.statistics.map((text, i) => {
							if ((scrollPart[i] === 0 && i > 0) || (scrollPart[i] === 1 && i < content.statistics.length - 1)) return null;
							let state = 'visible';
							if (scrollPart[i] < 0.1 && scrollPart[i] > 0 && i > 0) state = 'hidden';
							if (scrollPart[i] > 0.9 && i < content.statistics.length - 1) state = 'hidden';
							return (
								<div key={text.percentage} className={[s.doubleImages_inner_statistic, s[`doubleImages_inner_statistic__${state}`]].join(' ')}>
									<Heading content={{ rawText: text.percentage }} />
									<Heading content={{ rawText: translate(setup, text.caption) }} />
								</div>
							);
						})
					}
				</div>
			</div>
		</div>
	);
};

export default hot(module)(DoubleImages);
